import React from 'react';
const { useState } = React;
import { APTITUDES, DIMENSIONS, ENERGY_OPTIONS, CHANNELS, ZONES, NEXT_SERVICES, SERVICE_NAMES, P4_SOURCES, VOLUME_WHEEL_SIGNALS, classifyZone, t } from '../../diagnosticConfig.js';
import { Field, inputSt, textareaSt, cardSt, labelSt, mutedSt, badgeSt, tableSt, thSt, tdSt } from './shared.jsx';

// Ale's validation of P2 (aptitude levels of one person) and P4 (the 9
// dimensions, the 3 priorities and the next service). What she decides is
// saved in Validated Content, apart from the AI proposal, so changes can be audited.

// The client is already doing the Diagnostic: only the next services, by their names
export const SERVICE_LABELS = Object.fromEntries(NEXT_SERVICES.map(k => [k, SERVICE_NAMES[k]]));
const zoneLabel = (z) => t(ZONES[z]?.label) || '—';
const sourceLabel = (k) => t(P4_SOURCES.find(x => x.key === k)?.label) || k;
const signalLabel = (k) => t(VOLUME_WHEEL_SIGNALS.find(x => x.key === k)?.label) || k;
// P4 evidence: { source, text } (older drafts: plain text)
const evidenceText = (e) => (typeof e === 'string' ? e : `${sourceLabel(e.source)}: ${e.text}`);
const energyLabel = (k) => { const e = ENERGY_OPTIONS.find(x => x.key === k); return e ? `${e.symbol} ${t(e.label)}` : '—'; };
const levelLabel = (v) => ({ unknown: 'No lo sabe', not_applicable: 'No aplica', not_evaluated: 'Sin evaluación' })[v] || (v ?? '—');
const reviewRow = { background: 'rgba(245,158,11,0.08)' };
const smallInput = { ...inputSt, padding: '4px 6px', fontSize: 12 };

function LevelSelect({ value, onChange, disabled }) {
  return (
    <select style={smallInput} value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}>
      <option value="">Sin nivel</option>
      {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

function Footer({ editable, busy, onSave, onApprove, approveLabel, hint }) {
  if (!editable) return null;
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
      {hint && <span style={{ ...mutedSt, marginRight: 'auto' }}>{hint}</span>}
      <button className="action-btn" disabled={busy} onClick={onSave}>💾 Guardar</button>
      <button className="action-btn btn-primary" disabled={busy} onClick={onApprove}>✓ {approveLabel}</button>
    </div>
  );
}

// ── P2: aptitudes of one person ──

export function AptitudeValidation({ output, busy, onSave }) {
  const editable = output.status === 'draft' && output.isCurrent;
  const proposals = Object.fromEntries((output.content?.aptitudes || []).map(a => [a.aptitude, a]));
  const views = output.views || { aptitudes: [], channelRank: [] };
  const saved = output.validated || {};
  const [rows, setRows] = useState(() => Object.fromEntries(views.aptitudes.map(v => {
    const s = saved.aptitudes?.[v.key];
    const p = proposals[v.key];
    return [v.key, s
      ? { level: s.level, energy: s.energy, reason: s.reason || '', validated: !!s.validated }
      // The interview energy (P1) wins over the self-assessment when there is one; Ale sees both
      : { level: p?.proposed_level ?? null, energy: v.interviewEnergy ?? v.self.energy ?? null, reason: '', validated: false }];
  })));
  // The channel detected in the evidence (interview) wins over the self-assessment ranking; Ale sees both
  const selfChannel = views.channelRank[0] || null;
  const [channel, setChannel] = useState(saved.channel || { value: views.interviewChannel || selfChannel, reason: '' });
  const channelLabel = (k) => t(CHANNELS.find(c => c.key === k)?.label) || '—';
  const channelsDiffer = !!(views.interviewChannel && selfChannel && views.interviewChannel !== selfChannel);
  const set = (k, patch) => setRows(r => ({ ...r, [k]: { ...r[k], ...patch } }));
  const send = (approve) => onSave({ action: 'validateP2', outputId: output.id, aptitudes: rows, channel, approve });
  const pending = views.aptitudes.filter(v => !rows[v.key]?.validated).length;

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableSt}>
          <thead><tr>{['Aptitud', 'Autoevaluación', views.withManager ? 'Manager' : null, 'Ale preliminar', 'IA propone', 'Justificación de la IA', 'Nivel final', 'Energía', 'Zona final', 'Porqué', 'Validada']
            .filter(Boolean).map(h => <th key={h} style={thSt}>{h}</th>)}</tr></thead>
          <tbody>
            {views.aptitudes.map(v => {
              const p = proposals[v.key] || {};
              const row = rows[v.key];
              const changed = row.level !== (p.proposed_level ?? null);
              const needsReason = changed; // the reason is required only when Ale changes the proposal
              const energyDiffers = v.interviewEnergy && v.self.energy && v.interviewEnergy !== v.self.energy;
              return (
                <tr key={v.key} style={v.needsReview ? reviewRow : undefined}>
                  <td style={tdSt}><b>{t(APTITUDES.find(a => a.key === v.key)?.label)}</b>{v.needsReview && <div style={{ ...badgeSt(false), color: '#9A6212', marginTop: 4, display: 'inline-block' }}>Revisar</div>}</td>
                  <td style={tdSt}>{levelLabel(v.self.level)}<div style={mutedSt}>{energyLabel(v.self.energy)}</div></td>
                  {views.withManager && <td style={tdSt}>{levelLabel(v.manager)}</td>}
                  <td style={tdSt}>{levelLabel(v.ale?.level)}{v.ale?.evidence && <div style={{ ...mutedSt, maxWidth: 200 }}>{v.ale.evidence}</div>}</td>
                  <td style={tdSt}><b>{levelLabel(p.proposed_level)}</b></td>
                  <td style={{ ...tdSt, minWidth: 220 }}>{p.justification || '—'}</td>
                  <td style={tdSt}><LevelSelect value={row.level} disabled={!editable} onChange={level => set(v.key, { level })} /></td>
                  <td style={tdSt}>
                    <select style={smallInput} value={row.energy || ''} disabled={!editable} onChange={e => set(v.key, { energy: e.target.value || null })}>
                      <option value="">—</option>
                      {ENERGY_OPTIONS.map(e => <option key={e.key} value={e.key}>{e.symbol} {t(e.label)}</option>)}
                    </select>
                    {(energyDiffers || (v.interviewEnergy && !v.self.energy)) && (
                      <div style={{ ...mutedSt, fontSize: 11, marginTop: 4, color: energyDiffers ? '#9A6212' : undefined }}>
                        Entrevista: {energyLabel(v.interviewEnergy)}<br />Autoevaluación: {energyLabel(v.self.energy)}
                      </div>
                    )}
                  </td>
                  <td style={tdSt}>{zoneLabel(classifyZone(row.level, row.energy))}</td>
                  <td style={{ ...tdSt, minWidth: 200 }}>
                    <textarea style={{ ...textareaSt, minHeight: 40, fontSize: 12, ...(needsReason && !row.reason ? { borderColor: 'rgba(245,158,11,0.7)' } : {}) }}
                      value={row.reason} maxLength={2000} disabled={!editable} placeholder={needsReason ? 'Obligatorio' : 'Opcional'}
                      onChange={e => set(v.key, { reason: e.target.value })} />
                  </td>
                  <td style={{ ...tdSt, textAlign: 'center' }}>
                    <input type="checkbox" checked={row.validated} disabled={!editable} onChange={e => set(v.key, { validated: e.target.checked })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 12 }}>
        <Field label="Canal donde rinde más (validado)"
          hint={`Entrevista (P1): ${views.interviewChannel ? channelLabel(views.interviewChannel) : 'sin dato'} · Autoevaluación: ${views.channelRank.length ? views.channelRank.map(channelLabel).join(' › ') : 'sin dato'}`}>
          {channelsDiffer && <div style={{ ...badgeSt(false), color: '#9A6212', display: 'inline-block', marginBottom: 4 }}>Revisar: la entrevista y la autoevaluación no coinciden</div>}
          <select style={{ ...inputSt, ...(channelsDiffer ? { borderColor: 'rgba(245,158,11,0.7)' } : {}) }} value={channel.value || ''} disabled={!editable} onChange={e => setChannel({ ...channel, value: e.target.value || null })}>
            <option value="">—</option>
            {CHANNELS.map(c => <option key={c.key} value={c.key}>{t(c.label)}</option>)}
          </select>
        </Field>
        <Field label="Porqué del canal"><input style={inputSt} value={channel.reason || ''} disabled={!editable} onChange={e => setChannel({ ...channel, reason: e.target.value })} /></Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 12 }}>
        <TextList label="Fortalezas (IA)" items={output.content?.top_strengths} />
        <TextList label="Brechas a formar (IA)" items={output.content?.top_gaps} />
        <TextList label="Diferencias de percepción (IA)" items={output.content?.perception_gaps} />
      </div>

      <Footer editable={editable} busy={busy} onSave={() => send(false)} onApprove={() => send(true)} approveLabel="Aprobar niveles"
        hint={pending ? `Faltan ${pending} aptitudes por validar.` : 'Todas las aptitudes están validadas.'} />
      {editable && (
        <div style={{ textAlign: 'right', marginTop: 6 }}>
          <button className="action-btn" style={{ fontSize: 11 }} disabled={busy}
            onClick={() => setRows(r => Object.fromEntries(Object.entries(r).map(([k, x]) => [k, { ...x, validated: true }])))}>Marcar todas como validadas</button>
        </div>
      )}
    </div>
  );
}

export function TextList({ label, items }) {
  return (
    <div>
      <div style={labelSt}>{label}</div>
      {items?.length ? <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>{items.map((x, i) => <li key={i}>{x}</li>)}</ul> : <div style={mutedSt}>—</div>}
    </div>
  );
}

// ── P4: dimensions, priorities and next service ──

export function DimensionValidation({ output, busy, onSave }) {
  const editable = output.status === 'draft' && output.isCurrent;
  const content = output.content || {};
  const saved = output.validated || {};
  const proposals = Object.fromEntries((content.dimensions || []).map(d => [d.dimension, d]));
  const [dims, setDims] = useState(() => Object.fromEntries(DIMENSIONS.map(d => {
    const s = saved.dimensions?.[d.key];
    return [d.key, s ? { level: s.level, reason: s.reason || '', validated: !!s.validated }
      : { level: proposals[d.key]?.proposed_level ?? null, reason: '', validated: false }];
  })));
  // A service no longer offered (e.g. "diagnostic" in an older draft) starts empty
  const [priorities, setPriorities] = useState(() => (saved.priorities || content.priorities || [])
    .map(p => ({ ...p, service: SERVICE_LABELS[p.service] ? p.service : null })));
  const [service, setService] = useState(() => saved.service || {
    service: content.recommended_next_service?.service || null, reason: content.recommended_next_service?.reason || '', confirmed: false,
  });
  const setDim = (k, patch) => setDims(d => ({ ...d, [k]: { ...d[k], ...patch } }));
  const setPriority = (i, patch) => setPriorities(list => list.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const send = (approve) => onSave({ action: 'validateP4', outputId: output.id, dimensions: dims, priorities, service, approve });
  const pending = DIMENSIONS.filter(d => !dims[d.key]?.validated).length;

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableSt}>
          <thead><tr>{['Dimensión', 'IA propone', 'Evidencia', 'Contradicciones', 'Nivel final', 'Porqué', 'Validada'].map(h => <th key={h} style={thSt}>{h}</th>)}</tr></thead>
          <tbody>
            {DIMENSIONS.map(d => {
              const p = proposals[d.key] || {};
              const row = dims[d.key];
              const changed = row.level !== (p.proposed_level ?? null);
              return (
                <tr key={d.key} style={changed ? reviewRow : undefined}>
                  <td style={tdSt}><b>{d.number}. {t(d.label)}</b></td>
                  <td style={tdSt}><b>{p.proposed_level ?? '—'}</b></td>
                  <td style={{ ...tdSt, minWidth: 220 }}>{(p.evidence || []).map((e, i) => <div key={i}>• {evidenceText(e)}</div>)}</td>
                  <td style={{ ...tdSt, minWidth: 160 }}>{(p.contradictions || []).map((e, i) => <div key={i}>• {e}</div>)}</td>
                  <td style={tdSt}><LevelSelect value={row.level} disabled={!editable} onChange={level => setDim(d.key, { level })} /></td>
                  <td style={{ ...tdSt, minWidth: 200 }}>
                    <textarea style={{ ...textareaSt, minHeight: 40, fontSize: 12, ...(changed && !row.reason ? { borderColor: 'rgba(245,158,11,0.7)' } : {}) }}
                      value={row.reason} maxLength={2000} disabled={!editable} placeholder={changed ? 'Obligatorio' : 'Opcional'} onChange={e => setDim(d.key, { reason: e.target.value })} />
                  </td>
                  <td style={{ ...tdSt, textAlign: 'center' }}>
                    <input type="checkbox" checked={row.validated} disabled={!editable} onChange={e => setDim(d.key, { validated: e.target.checked })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ ...cardSt, marginTop: 12 }}>
        <div style={{ ...labelSt, marginBottom: 8 }}>Patrones detectados (IA)</div>
        {(content.patterns || []).length ? content.patterns.map((p, i) => (
          <div key={i} style={{ marginBottom: 8, fontSize: 13 }}>
            <b>{p.name === 'rueda_del_volumen' ? 'Rueda del volumen' : p.name}</b>
            {(p.signals_found || []).map((s, j) => <div key={j} style={{ marginLeft: 12 }}>• {signalLabel(s.signal)}: <span style={mutedSt}>{s.source ? `${sourceLabel(s.source)}: ` : ''}{s.evidence}</span></div>)}
          </div>
        )) : <div style={mutedSt}>Ninguno.</div>}
      </div>

      <div style={cardSt}>
        <div style={{ ...labelSt, marginBottom: 8 }}>Prioridades (máximo 3)</div>
        {priorities.map((p, i) => (
          <div key={i} style={{ borderBottom: '1px solid var(--globant-border)', paddingBottom: 10, marginBottom: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, alignItems: 'end' }}>
              <Field label={`${i + 1}. Título`}><input style={inputSt} value={p.title || ''} disabled={!editable} onChange={e => setPriority(i, { title: e.target.value })} /></Field>
              <Field label="Servicio">
                <select style={inputSt} value={p.service || ''} disabled={!editable} onChange={e => setPriority(i, { service: e.target.value || null })}>
                  <option value="">—</option>
                  {Object.entries(SERVICE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </Field>
              {editable && <button className="action-btn" style={{ fontSize: 11 }} onClick={() => setPriorities(list => list.filter((_, j) => j !== i))}>Quitar</button>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
              <Field label="Qué pasa"><textarea style={textareaSt} value={p.what || ''} disabled={!editable} onChange={e => setPriority(i, { what: e.target.value })} /></Field>
              <Field label="Por qué importa"><textarea style={textareaSt} value={p.why || ''} disabled={!editable} onChange={e => setPriority(i, { why: e.target.value })} /></Field>
            </div>
          </div>
        ))}
        {editable && priorities.length < 3 && (
          <button className="action-btn" style={{ fontSize: 12 }} onClick={() => setPriorities(list => [...list, { title: '', what: '', why: '', service: null }])}>➕ Agregar prioridad</button>
        )}
      </div>

      <div style={cardSt}>
        <div style={{ ...labelSt, marginBottom: 8 }}>Siguiente servicio recomendado</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
          <Field label="Servicio">
            <select style={inputSt} value={service.service || ''} disabled={!editable} onChange={e => setService({ ...service, service: e.target.value || null })}>
              <option value="">—</option>
              {['systemDesign', 'enablement', 'advisory'].map(k => <option key={k} value={k}>{SERVICE_LABELS[k]}</option>)}
            </select>
          </Field>
          <Field label="Motivo"><input style={inputSt} value={service.reason || ''} disabled={!editable} onChange={e => setService({ ...service, reason: e.target.value })} /></Field>
        </div>
        <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <input type="checkbox" checked={!!service.confirmed} disabled={!editable} onChange={e => setService({ ...service, confirmed: e.target.checked })} />
          Confirmo el servicio recomendado
        </label>
      </div>

      <Footer editable={editable} busy={busy} onSave={() => send(false)} onApprove={() => send(true)} approveLabel="Aprobar P4"
        hint={pending ? `Faltan ${pending} dimensiones por validar.` : 'Las 9 dimensiones están validadas.'} />
    </div>
  );
}
